/* PRINCE.PAW — Atelier theme
   Motion + interactivity. Progressive enhancement only:
   every feature no-ops gracefully without JS or with reduced motion. */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveals (staggered) ---------- */

  function initReveals() {
    var pending = Array.prototype.slice.call(document.querySelectorAll('[data-reveal]'));
    if (!pending.length) return;

    function show(el) {
      var delay = el.getAttribute('data-reveal-delay');
      if (delay) el.style.setProperty('--reveal-delay', delay + 'ms');
      el.classList.add('in');
    }

    if (reduced || !('IntersectionObserver' in window)) {
      pending.forEach(show);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        show(entry.target);
        io.unobserve(entry.target);
        var i = pending.indexOf(entry.target);
        if (i > -1) pending.splice(i, 1);
      });
    }, { threshold: 0, rootMargin: '0px 0px -5% 0px' });

    pending.forEach(function (el) { io.observe(el); });

    /* Safety net. An animation must never leave content permanently invisible:
       if the observer does not fire (frozen rAF, odd embedding, tall elements),
       sweep anything that has scrolled into view and reveal it directly. */
    var sweeping = false;
    function sweep() {
      sweeping = false;
      if (!pending.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        return;
      }
      var vh = window.innerHeight;
      for (var i = pending.length - 1; i >= 0; i--) {
        var el = pending[i];
        var r = el.getBoundingClientRect();
        if (r.top < vh * 0.95 && r.bottom > 0) {
          show(el);
          io.unobserve(el);
          pending.splice(i, 1);
        }
      }
    }
    function onScroll() {
      if (sweeping) return;
      sweeping = true;
      requestAnimationFrame(sweep);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', sweep);
    setTimeout(sweep, 600);
    setTimeout(sweep, 2000);
  }

  /* ---------- Hero slider ---------- */

  function initHeroSlider() {
    document.querySelectorAll('[data-hero-slider]').forEach(function (root) {
      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-dot]'));
      if (slides.length < 2) return;

      var index = 0;
      var timer = null;
      var delay = parseInt(root.getAttribute('data-autoplay'), 10) || 0;

      function go(next) {
        next = (next + slides.length) % slides.length;
        if (next === index) return;
        slides[index].classList.remove('is-active');
        slides[next].classList.add('is-active');
        if (dots[index]) dots[index].setAttribute('aria-current', 'false');
        if (dots[next]) dots[next].setAttribute('aria-current', 'true');
        index = next;
      }

      function stop() { if (timer) { clearInterval(timer); timer = null; } root.classList.add('is-paused'); }
      function start() {
        if (!delay || reduced || timer) return;
        root.classList.remove('is-paused');
        timer = setInterval(function () { go(index + 1); }, delay);
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { stop(); go(i); start(); });
      });

      root.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { stop(); go(index + 1); start(); }
        else if (e.key === 'ArrowLeft') { stop(); go(index - 1); start(); }
      });

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(); else start();
      });

      /* Touch swipe */
      var startX = null;
      root.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; stop(); }, { passive: true });
      root.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 45) go(index + (dx < 0 ? 1 : -1));
        startX = null;
        start();
      }, { passive: true });

      start();
    });
  }

  /* ---------- Sticky header: hide on scroll down, reveal on up ---------- */

  function initHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;
    var lastY = window.scrollY;
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = window.scrollY;
        header.classList.toggle('is-scrolled', y > 8);
        if (!reduced) {
          if (y > lastY && y > 220) header.classList.add('is-hidden');
          else header.classList.remove('is-hidden');
        }
        lastY = y;
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Parallax media ---------- */

  function initParallax() {
    if (reduced) return;
    var wraps = document.querySelectorAll('.parallax-wrap');
    if (!wraps.length) return;
    var active = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var i = active.indexOf(entry.target);
        if (entry.isIntersecting && i === -1) active.push(entry.target);
        else if (!entry.isIntersecting && i > -1) active.splice(i, 1);
      });
    });
    wraps.forEach(function (w) { io.observe(w); });

    var raf = null;
    function frame() {
      active.forEach(function (w) {
        var img = w.querySelector('img');
        if (!img) return;
        var rect = w.getBoundingClientRect();
        var progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        var speed = parseFloat(w.getAttribute('data-parallax') || '0.12');
        img.style.transform = 'translate3d(0,' + (progress * speed * -100).toFixed(2) + 'px,0)';
      });
      raf = null;
    }
    window.addEventListener('scroll', function () {
      if (raf === null) raf = requestAnimationFrame(frame);
    }, { passive: true });
    frame();
  }

  /* ---------- Magnetic buttons ---------- */

  function initMagnetic() {
    if (reduced || !window.matchMedia('(pointer: fine)').matches) return;
    document.querySelectorAll('[data-magnetic]').forEach(function (el) {
      var strength = 0.25;
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        el.style.transform = 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transition = 'transform 0.5s cubic-bezier(0.16,1,0.3,1)';
        el.style.transform = '';
        setTimeout(function () { el.style.transition = ''; }, 500);
      });
    });
  }

  /* ---------- Mobile navigation ---------- */

  function initMobileNav() {
    var toggle = document.querySelector('.nav-toggle');
    var panel = document.querySelector('.mobile-nav');
    if (!toggle || !panel) return;
    var close = panel.querySelector('.mobile-nav__close');
    function setOpen(open) {
      panel.classList.toggle('open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.documentElement.style.overflow = open ? 'hidden' : '';
    }
    toggle.addEventListener('click', function () { setOpen(!panel.classList.contains('open')); });
    if (close) close.addEventListener('click', function () { setOpen(false); });
    panel.addEventListener('click', function (e) { if (e.target.tagName === 'A') setOpen(false); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') setOpen(false); });
  }

  /* ---------- Product gallery ---------- */

  function initGallery() {
    document.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      var main = gallery.querySelector('.product-gallery__main img');
      var thumbs = gallery.querySelectorAll('.product-gallery__thumbs button');
      if (!main || !thumbs.length) return;
      thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          thumbs.forEach(function (b) { b.classList.remove('active'); });
          btn.classList.add('active');
          var src = btn.getAttribute('data-src');
          var srcset = btn.getAttribute('data-srcset');
          if (src) main.src = src;
          if (srcset) main.srcset = srcset;
          if (!reduced) {
            main.animate(
              [{ opacity: 0.35, transform: 'scale(1.015)' }, { opacity: 1, transform: 'scale(1)' }],
              { duration: 450, easing: 'cubic-bezier(0.16,1,0.3,1)' }
            );
          }
        });
      });
    });
  }

  /* ---------- Quantity steppers ---------- */

  function initQty() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-change]');
      if (!btn) return;
      var input = btn.parentElement.querySelector('input');
      if (!input) return;
      var step = parseInt(btn.getAttribute('data-qty-change'), 10);
      var min = parseInt(input.min || '0', 10);
      var next = Math.max(min, (parseInt(input.value, 10) || 0) + step);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- AJAX add to cart (falls back to normal form post) ---------- */

  function initCartForm() {
    document.querySelectorAll('form[data-ajax-cart]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        var label = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = btn.getAttribute('data-adding-label') || label; }

        fetch(form.action + '.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' }
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'error'); });
            return fetch('/cart.js').then(function (c) { return c.json(); });
          })
          .then(function (cart) {
            document.querySelectorAll('.cart-count').forEach(function (el) {
              el.textContent = cart.item_count;
              el.classList.remove('bump');
              void el.offsetWidth;
              el.classList.add('bump');
            });
            if (btn) { btn.textContent = btn.getAttribute('data-added-label') || label; }
            setTimeout(function () { if (btn) { btn.disabled = false; btn.textContent = label; } }, 1800);
          })
          .catch(function () {
            /* fall back to standard submission so the customer is never blocked */
            if (btn) { btn.disabled = false; btn.textContent = label; }
            form.removeAttribute('data-ajax-cart');
            form.submit();
          });
      });
    });
  }

  /* ---------- Cart page: auto-submit quantity updates ---------- */

  function initCartPage() {
    var cartForm = document.querySelector('form[data-cart-page]');
    if (!cartForm) return;
    cartForm.addEventListener('change', function (e) {
      if (e.target.matches('input[name="updates[]"]')) cartForm.submit();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveals();
    initHeroSlider();
    initHeader();
    initParallax();
    initMagnetic();
    initMobileNav();
    initGallery();
    initQty();
    initCartForm();
    initCartPage();
  });
})();
